import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import api, { formatINR, formatDate, formatDateTime, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DeliveryStatusBadge, PayStatusBadge, PaymentBadge, SpotPayBadge } from "@/components/badges";
import PaymentCollectionModal from "@/components/PaymentCollectionModal";
import StartDeliveryModal from "@/components/StartDeliveryModal";
import CompleteDeliveryModal from "@/components/CompleteDeliveryModal";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Search, ChevronDown, Check, Wallet, Trash2, Truck, Receipt, X, PlayCircle, CheckCircle2, Clock, Timer, Ban, Send, MessageCircle } from "lucide-react";
import { openWhatsApp, deliveryShareMessage } from "@/lib/whatsapp";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";

const SLOW_THRESHOLD_MIN = 240; // deliveries taking longer than 4h are flagged slow

function durationInfo(started, completed) {
  if (!started || !completed) return null;
  const mins = Math.max(0, Math.round((new Date(completed) - new Date(started)) / 60000));
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  const label = h > 0 ? `${h}h ${m}m` : `${m}m`;
  return { mins, label, slow: mins > SLOW_THRESHOLD_MIN };
}

const STATUSES = ["Assigned", "Out_for_Delivery", "Incomplete", "Delivered", "Cancelled"];

function payRef(p) {
  if (p.mode === "UPI") return p.reference || "—";
  if (p.mode === "Card") return p.card_last4 ? `•••• ${p.card_last4}` : "—";
  if (p.mode === "Cheque") return [p.reference, p.bank].filter(Boolean).join(" · ") || "—";
  if (p.mode === "Credit") return p.due_date ? `Due ${formatDate(p.due_date)}` : "Pay later";
  return "—";
}

function TimeStat({ icon: Icon, label, value, done }) {
  return (
    <div className="flex items-center gap-2 rounded-lg border bg-card px-3 py-2">
      <Icon className={`h-4 w-4 ${done ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"}`} />
      <div className="min-w-0">
        <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="truncate text-xs font-medium">{value}</div>
      </div>
    </div>
  );
}

function DeliveryPayments({ delivery, onCollect }) {
  const { data, isLoading } = useQuery({
    queryKey: ["delivery-detail", delivery.id],
    queryFn: async () => (await api.get(`/deliveries/${delivery.id}`)).data,
  });
  const payments = data?.payments || [];
  return (
    <div className="pt-2" data-testid={`delivery-payments-${delivery.id}`}>
      <div className="flex items-center justify-between">
        <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Payment History</p>
        {delivery.outstanding > 0 && (
          <Button variant="ghost" size="sm" className="h-7 text-primary" onClick={onCollect} data-testid={`add-payment-inline-${delivery.id}`}>
            <Wallet className="mr-1.5 h-3.5 w-3.5" /> Add payment
          </Button>
        )}
      </div>
      {isLoading ? (
        <p className="py-2 text-xs text-muted-foreground">Loading…</p>
      ) : payments.length === 0 ? (
        <p className="rounded-lg border border-dashed bg-card px-3 py-3 text-center text-xs text-muted-foreground">No payments recorded yet</p>
      ) : (
        <div className="space-y-2">
          {payments
            .slice()
            .sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""))
            .map((p, pi) => (
              <div key={pi} className="flex items-center gap-3 rounded-lg border bg-card px-3 py-2 text-sm" data-testid={`payment-history-item-${delivery.id}-${pi}`}>
                <span className="font-mono text-xs text-muted-foreground">{formatDate(p.created_at)}</span>
                <PaymentBadge mode={p.mode} />
                <span className="min-w-0 flex-1 truncate font-mono text-xs text-muted-foreground">{payRef(p)}</span>
                <span className="hidden text-xs text-muted-foreground sm:inline">by {p.collected_by}</span>
                <span className={`font-mono font-semibold ${p.mode === "Credit" ? "text-rose-600 dark:text-rose-400" : "text-emerald-600 dark:text-emerald-400"}`}>
                  {p.mode === "Credit" ? "" : "+"}{formatINR(p.amount)}
                </span>
              </div>
            ))}
          <div className="flex justify-end gap-6 px-3 pt-1 font-mono text-xs">
            <span className="text-muted-foreground">Collected <span className="font-semibold text-foreground">{formatINR(delivery.paid_amount)}</span></span>
            <span className="text-muted-foreground">Outstanding <span className={`font-semibold ${delivery.outstanding > 0 ? "text-rose-600 dark:text-rose-400" : "text-foreground"}`}>{formatINR(delivery.outstanding)}</span></span>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Deliveries() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [payStatus, setPayStatus] = useState("all");
  const [areaFilter, setAreaFilter] = useState("all");
  const [expanded, setExpanded] = useState(null);
  const [payFor, setPayFor] = useState(null);
  const [startFor, setStartFor] = useState(null);
  const [completeFor, setCompleteFor] = useState(null);
  const [dialog, setDialog] = useState(false);

  const [customerId, setCustomerId] = useState("");
  const [custOpen, setCustOpen] = useState(false);
  const [bills, setBills] = useState([{ invoice_number: "", amount: "", note: "" }]);
  const [spotPay, setSpotPay] = useState(false);
  const [spotMode, setSpotMode] = useState("Cash");
  const [spotAmount, setSpotAmount] = useState("");
  const [cancelFor, setCancelFor] = useState(null);
  const [cancelReason, setCancelReason] = useState("");

  const { data: deliveries = [], isLoading } = useQuery({
    queryKey: ["deliveries", search, status, payStatus, areaFilter],
    queryFn: async () =>
      (await api.get("/deliveries", { params: { search, status, payment_status: payStatus, area: areaFilter } })).data,
  });
  const { data: customers = [] } = useQuery({ queryKey: ["customers-all"], queryFn: async () => (await api.get("/customers")).data });
  const { data: drivers = [] } = useQuery({ queryKey: ["drivers"], queryFn: async () => (await api.get("/delivery-persons")).data });
  const { data: areas = [] } = useQuery({ queryKey: ["areas"], queryFn: async () => (await api.get("/customers/areas")).data });

  const create = useMutation({
    mutationFn: async () =>
      api.post("/deliveries", {
        customer_id: customerId,
        bills: bills.map((b) => ({ invoice_number: b.invoice_number, amount: parseFloat(b.amount) || 0, note: b.note })),
        status: "Assigned",
        spot_pay: spotPay,
        spot_mode: spotMode,
        spot_amount: spotPay ? parseFloat(spotAmount) || 0 : 0,
      }),
    onSuccess: () => {
      toast.success(spotPay ? "Delivery created with SPOT PAY recorded" : "Delivery created — assign a person when ready to dispatch");
      qc.invalidateQueries({ queryKey: ["deliveries"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["payments"] });
      setDialog(false);
      setCustomerId(""); setBills([{ invoice_number: "", amount: "", note: "" }]);
      setSpotPay(false); setSpotMode("Cash"); setSpotAmount("");
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const del = useMutation({
    mutationFn: (id) => api.delete(`/deliveries/${id}`),
    onSuccess: () => {
      toast.success("Delivery deleted");
      qc.invalidateQueries({ queryKey: ["deliveries"] });
    },
  });
  const returnSent = useMutation({
    mutationFn: (id) => api.post(`/deliveries/${id}/return-sent`),
    onSuccess: () => {
      toast.success("Returned stock marked as sent back — now complete the delivery");
      qc.invalidateQueries({ queryKey: ["deliveries"] });
    },
  });
  const cancel = useMutation({
    mutationFn: () => api.post(`/deliveries/${cancelFor.id}/cancel`, { reason: cancelReason }),
    onSuccess: () => {
      toast.success("Order cancelled");
      qc.invalidateQueries({ queryKey: ["deliveries"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      setCancelFor(null); setCancelReason("");
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const billTotal = bills.reduce((s, b) => s + (parseFloat(b.amount) || 0), 0);
  const updBill = (i, k, v) => setBills(bills.map((b, idx) => (idx === i ? { ...b, [k]: v } : b)));

  return (
    <div className="space-y-6" data-testid="deliveries-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Deliveries</h1>
          <p className="mt-1 text-sm text-muted-foreground">{deliveries.length} deliveries · multi-bill dispatch &amp; payment</p>
        </div>
        <Button onClick={() => setDialog(true)} data-testid="new-delivery-button">
          <Plus className="mr-2 h-4 w-4" /> New Delivery
        </Button>
      </div>

      <Card className="p-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-9" placeholder="Search name, customer ID or invoice…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="delivery-search-input" />
          </div>
          <Select value={areaFilter} onValueChange={setAreaFilter}>
            <SelectTrigger data-testid="delivery-area-filter"><SelectValue placeholder="Area" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Areas</SelectItem>{areas.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="delivery-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={payStatus} onValueChange={setPayStatus}>
            <SelectTrigger data-testid="delivery-payment-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Payments</SelectItem>
              {["Pending", "Partial", "Paid", "Credit"].map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </Card>

      <div className="space-y-3">
        {isLoading ? (
          <div className="h-40 animate-pulse rounded-xl bg-secondary" />
        ) : deliveries.length === 0 ? (
          <Card className="py-16 text-center text-muted-foreground">No deliveries found</Card>
        ) : (
          deliveries.map((d, i) => (
            <motion.div key={d.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.02, 0.25) }}>
              <Card className="overflow-hidden" data-testid={`delivery-card-${d.id}`}>
                <div className="flex flex-wrap items-center gap-4 p-4">
                  <button className="flex flex-1 items-center gap-3 text-left" onClick={() => setExpanded(expanded === d.id ? null : d.id)}>
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <Receipt className="h-5 w-5" />
                    </span>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{d.customer_name}</span>
                        <span className="rounded bg-secondary px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
                          {d.bills.length} {d.bills.length > 1 ? "bills" : "bill"}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span>{d.customer_area}</span>·<span className="flex items-center gap-1"><Truck className="h-3 w-3" />{d.driver_name}</span>·<span>{formatDate(d.date)}</span>
                      </div>
                    </div>
                    <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${expanded === d.id ? "rotate-180" : ""}`} />
                  </button>

                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="font-mono font-semibold">{formatINR(d.total_amount)}</div>
                      {d.outstanding > 0 && <div className="font-mono text-xs text-rose-600 dark:text-rose-400">Due {formatINR(d.outstanding)}</div>}
                    </div>
                    <div className="hidden sm:block"><DeliveryStatusBadge status={d.status} /></div>
                    <PayStatusBadge status={d.payment_status} />
                    {d.spot_pay && <SpotPayBadge />}
                    {(() => {
                      const dur = durationInfo(d.started_at, d.completed_at);
                      if (!dur) return null;
                      return (
                        <span
                          data-testid={`delivery-duration-${d.id}`}
                          className={`hidden items-center gap-1 rounded-full border px-2 py-0.5 font-mono text-xs md:inline-flex ${dur.slow ? "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-300" : "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300"}`}
                          title={dur.slow ? "Slow delivery" : "Delivery duration"}
                        >
                          <Timer className="h-3 w-3" />{dur.label}{dur.slow ? " · slow" : ""}
                        </span>
                      );
                    })()}
                  </div>

                  <div className="flex items-center gap-1.5">
                    <Button
                      variant="ghost"
                      size="icon"
                      title="Share invoice & balance via WhatsApp"
                      data-testid={`share-whatsapp-${d.id}`}
                      onClick={() => {
                        if (!openWhatsApp(d.customer_phone, deliveryShareMessage(d))) toast.error("No phone number for this customer");
                      }}
                    >
                      <MessageCircle className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                    </Button>
                    {d.status === "Assigned" && (
                      <Button size="sm" onClick={() => setStartFor(d)} data-testid={`start-delivery-button-${d.id}`}>
                        <PlayCircle className="mr-1.5 h-4 w-4" /> Start
                      </Button>
                    )}
                    {d.status === "Out_for_Delivery" && (
                      <Button size="sm" onClick={() => setCompleteFor(d)} data-testid={`complete-delivery-button-${d.id}`}>
                        <CheckCircle2 className="mr-1.5 h-4 w-4" /> Complete
                      </Button>
                    )}
                    {d.status === "Incomplete" && !d.return_sent && (
                      <Button size="sm" variant="outline" onClick={() => returnSent.mutate(d.id)} data-testid={`return-sent-button-${d.id}`}>
                        <Send className="mr-1.5 h-4 w-4" /> Mark Return Sent
                      </Button>
                    )}
                    {d.status === "Incomplete" && d.return_sent && (
                      <Button size="sm" onClick={() => setCompleteFor(d)} data-testid={`complete-delivery-button-${d.id}`}>
                        <CheckCircle2 className="mr-1.5 h-4 w-4" /> Complete
                      </Button>
                    )}
                    {d.status === "Delivered" && d.outstanding > 0 && (
                      <Button size="sm" onClick={() => setPayFor(d)} data-testid={`collect-payment-button-${d.id}`}>
                        <Wallet className="mr-1.5 h-4 w-4" /> Collect
                      </Button>
                    )}
                    {["Assigned", "Out_for_Delivery", "Incomplete"].includes(d.status) && (
                      <Button variant="ghost" size="icon" onClick={() => setCancelFor(d)} data-testid={`cancel-delivery-button-${d.id}`} title="Cancel order">
                        <Ban className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                    {user?.role === "admin" && (
                      <Button variant="ghost" size="icon" onClick={() => del.mutate(d.id)} data-testid={`delivery-delete-${d.id}`}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                </div>

                <AnimatePresence>
                  {expanded === d.id && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden border-t bg-secondary/30">
                      <div className="space-y-2 p-4">
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
                          <TimeStat icon={Receipt} label="Created" value={formatDateTime(d.date)} done />
                          <TimeStat icon={PlayCircle} label="Dispatch Started" value={d.started_at ? formatDateTime(d.started_at) : "Not started"} done={!!d.started_at} />
                          <TimeStat icon={CheckCircle2} label="Completed" value={d.completed_at ? formatDateTime(d.completed_at) : "Pending"} done={!!d.completed_at} />
                          {(() => {
                            const dur = durationInfo(d.started_at, d.completed_at);
                            return (
                              <div className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${dur?.slow ? "border-rose-300 bg-rose-50 dark:border-rose-800 dark:bg-rose-950/30" : "bg-card"}`}>
                                <Timer className={`h-4 w-4 ${dur ? (dur.slow ? "text-rose-600 dark:text-rose-400" : "text-emerald-600 dark:text-emerald-400") : "text-muted-foreground"}`} />
                                <div className="min-w-0">
                                  <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Duration</div>
                                  <div className="truncate text-xs font-medium">{dur ? `${dur.label}${dur.slow ? " · slow" : ""}` : "—"}</div>
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                        {d.memo && (
                          <div className="rounded-lg border-l-2 border-primary bg-card px-3 py-2">
                            <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Memo</div>
                            <p className="text-sm">{d.memo}</p>
                          </div>
                        )}
                        <p className="pt-1 text-xs font-mono uppercase tracking-wider text-muted-foreground">Bills / Invoices</p>
                        {d.bills.map((b, bi) => (
                          <div key={bi} className="flex items-center justify-between rounded-lg border bg-card px-3 py-2 text-sm">
                            <span className="font-mono font-medium">{b.invoice_number}</span>
                            <span className="text-muted-foreground">{b.note}</span>
                            <span className="font-mono">{formatINR(b.amount)}</span>
                          </div>
                        ))}
                        <DeliveryPayments delivery={d} onCollect={() => setPayFor(d)} />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </Card>
            </motion.div>
          ))
        )}
      </div>

      <PaymentCollectionModal delivery={payFor} open={!!payFor} onClose={() => setPayFor(null)} />
      <StartDeliveryModal delivery={startFor} drivers={drivers} open={!!startFor} onClose={() => setStartFor(null)} />
      <CompleteDeliveryModal delivery={completeFor} open={!!completeFor} onClose={() => setCompleteFor(null)} />

      <Dialog open={!!cancelFor} onOpenChange={(o) => !o && setCancelFor(null)}>
        <DialogContent data-testid="cancel-delivery-dialog">
          <DialogHeader>
            <DialogTitle>Cancel Order</DialogTitle>
            <DialogDescription>Cancelling {cancelFor?.customer_name}'s order. A reason is required and saved for reports.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Reason for cancellation</Label>
            <Textarea value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder="e.g. Customer cancelled, out of stock, duplicate order…" data-testid="cancel-reason-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelFor(null)}>Back</Button>
            <Button variant="destructive" onClick={() => cancel.mutate()} disabled={!cancelReason.trim() || cancel.isPending} data-testid="cancel-confirm-button">
              <Ban className="mr-2 h-4 w-4" /> Cancel Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="delivery-dialog">
          <DialogHeader>
            <DialogTitle>New Delivery</DialogTitle>
            <DialogDescription>Add a customer and one or more bills. A delivery person is assigned later when dispatching.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Customer</Label>
              <Popover open={custOpen} onOpenChange={setCustOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-between font-normal" data-testid="delivery-customer-select">
                    <span className="truncate">
                      {customerId
                        ? (() => { const c = customers.find((x) => x.id === customerId); return c ? `${c.code} · ${c.name} — ${c.area}` : "Select customer"; })()
                        : "Search & select customer…"}
                    </span>
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                  <Command filter={(value, search) => (value.toLowerCase().includes(search.toLowerCase().trim()) ? 1 : 0)}>
                    <CommandInput placeholder="Type name, ID, area or phone…" data-testid="delivery-customer-search" />
                    <CommandList>
                      <CommandEmpty>No customer found</CommandEmpty>
                      <CommandGroup>
                        {customers.map((c) => (
                          <CommandItem
                            key={c.id}
                            value={`${c.code} ${c.name} ${c.area} ${c.phone || ""}`}
                            onSelect={() => { setCustomerId(c.id); setCustOpen(false); }}
                            data-testid={`delivery-customer-option-${c.id}`}
                          >
                            <Check className={`mr-2 h-4 w-4 shrink-0 ${customerId === c.id ? "opacity-100" : "opacity-0"}`} />
                            <span className="flex-1 truncate">{c.code} · {c.name} — {c.area}</span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Bills / Invoices</Label>
                <Button variant="outline" size="sm" onClick={() => setBills([...bills, { invoice_number: "", amount: "", note: "" }])} data-testid="add-bill-button">
                  <Plus className="mr-1 h-3 w-3" /> Add bill
                </Button>
              </div>
              {bills.map((b, i) => (
                <div key={i} className="grid grid-cols-12 gap-2">
                  <Input className="col-span-4" placeholder="Invoice #" value={b.invoice_number} onChange={(e) => updBill(i, "invoice_number", e.target.value)} data-testid={`bill-invoice-${i}`} />
                  <Input className="col-span-4" placeholder="Note" value={b.note} onChange={(e) => updBill(i, "note", e.target.value)} data-testid={`bill-note-${i}`} />
                  <Input className="col-span-3 font-mono" type="number" placeholder="Amount" value={b.amount} onChange={(e) => updBill(i, "amount", e.target.value)} data-testid={`bill-amount-${i}`} />
                  <button className="col-span-1 flex items-center justify-center text-muted-foreground hover:text-destructive" onClick={() => bills.length > 1 && setBills(bills.filter((_, idx) => idx !== i))} data-testid={`bill-remove-${i}`}>
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
              <div className="flex justify-end pt-1 font-mono text-sm">
                Total: <span className="ml-2 font-semibold">{formatINR(billTotal)}</span>
              </div>
            </div>

            <div className="rounded-lg border bg-secondary/40 p-3">
              <label className="flex cursor-pointer items-center gap-2 text-sm font-medium">
                <input type="checkbox" checked={spotPay} onChange={(e) => setSpotPay(e.target.checked)} data-testid="spot-pay-toggle" className="h-4 w-4 accent-primary" />
                Payment done at shop (SPOT PAY)
              </label>
              {spotPay && (
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <Select value={spotMode} onValueChange={setSpotMode}>
                    <SelectTrigger data-testid="spot-mode-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{["Cash", "UPI", "Card", "Cheque"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input type="number" className="font-mono" placeholder="Amount paid" value={spotAmount} onChange={(e) => setSpotAmount(e.target.value)} data-testid="spot-amount-input" />
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Cancel</Button>
            <Button onClick={() => create.mutate()} disabled={!customerId || billTotal <= 0 || create.isPending} data-testid="delivery-save-button">
              Create Delivery
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
